import * as Dialog from "@radix-ui/react-dialog";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { CalendarIcon } from "@radix-ui/react-icons";
import { Separator } from "@radix-ui/react-separator";
import { type CalendarEvent, google, ics } from "calendar-link";
import clsx from "clsx";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import Skeleton from "react-loading-skeleton";
import { Link } from "react-router-dom";

import { formatTime } from "~/common/formatters";
import { buildLocale } from "~/common/locale";
import { formatPersonName } from "~/components/common/PersonName";
import { Button } from "~/components/ui/button";
import { DevToolsOnly } from "~/features/dev-tools/dev-tools-only";
import { ZoomRedirectDialog } from "~/features/events/event-card";
import type { Loading } from "~/types/common";

import { ExpertImage, ExpertName, MeetingsTag } from "./meetings-shared";
import type { TMeeting } from "./meetings-types";

export function MeetingCard({
  meeting,
  padding = true,
  separator = true,
  shadow = true,
  dimFinished = true,
  displayDateBadges = true,
  displayDetailLink = true,
  displayJoinLink = true,
  displayActions = true,
}: {
  meeting: Loading<TMeeting>;
  padding?: boolean;
  separator?: boolean;
  shadow?: boolean;
  dimFinished?: boolean;
  displayDateBadges?: boolean;
  displayDetailLink?: boolean;
  displayJoinLink?: boolean;
  displayActions?: boolean;
}) {
  return (
    <article
      className={clsx("tw-flex tw-flex-col tw-gap-6 tw-rounded-3xl tw-bg-white tw-transition-all", {
        "hover:tw-opacity-100 sm:tw-opacity-70": meeting?.is_finished && dimFinished,
        "tw-p-5 sm:tw-p-7": padding,
        "tw-shadow-sm": shadow,
      })}
    >
      <div className="tw-relative tw-flex tw-flex-col tw-gap-6">
        {displayDateBadges ? <MeetingDateBadges meeting={meeting} /> : null}

        <div className="tw-flex tw-justify-between tw-gap-4">
          <MeetingExpertName meeting={meeting} />
          <ExpertImage expert={meeting?.expert} />
        </div>

        <DevToolsOnly>
          {meeting && displayDateBadges ? (
            <div className="tw-absolute -tw-top-2 tw-right-0 tw-text-2xl tw-font-semibold">
              {meeting.id}
            </div>
          ) : null}
        </DevToolsOnly>
      </div>

      <Separator
        className={clsx("tw-h-px tw-bg-[#D9DCE7]", { "desktop-lg:tw-hidden": !separator })}
      />

      <div className="tw-mt-auto tw-flex tw-flex-col tw-gap-8">
        {displayActions ? <MeetingActionLinks meeting={meeting} /> : null}

        <div className="tw-flex tw-justify-between tw-gap-2 tw-text-base max-sm:tw-flex-col">
          {displayDetailLink ? <MeetingDetailsLink meeting={meeting} /> : null}
          {displayJoinLink ? <MeetingJoinLink meeting={meeting} /> : null}
        </div>
      </div>
    </article>
  );
}

function MeetingExpertName({ meeting }: { meeting: Loading<TMeeting> }) {
  return (
    <div className="tw-flex tw-flex-col tw-justify-center tw-gap-2">
      <span className="tw-text-brand-gray sm:tw-text-base">
        {meeting ? (
          <FormattedMessage id="meetings.expert" />
        ) : (
          <Skeleton className="tw-w-[100px] tw-rounded-md sm:tw-w-[175px]" height={20} />
        )}
      </span>

      {meeting ? (
        <ExpertName expert={meeting.expert} />
      ) : (
        <Skeleton className="tw-rounded-md" width={150} height={30} />
      )}
    </div>
  );
}

function MeetingActionLinks({ meeting }: { meeting: Loading<TMeeting> }) {
  return (
    <div className="tw-flex tw-justify-between">
      <CalendarButton meeting={meeting} />

      {meeting ? (
        meeting.is_finished ? null : (
          <ActionLink
            id="reschedule-btn"
            href={meeting.reschedule_url_client}
            icon={<RescheduleIcon />}
          >
            <FormattedMessage id="meetings.reschedule" />
          </ActionLink>
        )
      ) : (
        <Skeleton className="tw-rounded-2xl" width={56} height={56} />
      )}

      {meeting ? (
        meeting.is_finished ? null : (
          <ActionLink id="cancel-btn" href={meeting.cancel_url_client} icon={<CancelIcon />}>
            <FormattedMessage id="meetings.cancel" />
          </ActionLink>
        )
      ) : (
        <Skeleton className="tw-rounded-2xl" width={56} height={56} />
      )}
    </div>
  );
}

function CalendarButton({ meeting }: { meeting: Loading<TMeeting> }) {
  if (!meeting) {
    return <Skeleton className="tw-rounded-2xl" width={56} height={56} />;
  }

  if (meeting.is_finished) {
    return null;
  }

  const id = `calendar-btn-${meeting.id}`;

  const calendarEvent: CalendarEvent = {
    title: getCalendarTitle(meeting),
    description: getCalendarDescription(meeting),
    start: meeting.start_time_utc,
    end: meeting.end_time_utc,
    location: meeting.zoom_join_url ?? meeting.gmeet_url ?? "",
    organizer: {
      email: meeting.expert.email,
      name: formatPersonName({ person: meeting.expert, useMiddleName: true }),
    },
  };

  return (
    <div className="tw-flex tw-max-w-[70px] tw-flex-col tw-items-center tw-gap-1">
      <DropdownMenu.Root modal={false}>
        <DropdownMenu.Trigger asChild>
          <button
            className={clsx(
              "tw-inline-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-2xl",
              "tw-border-none tw-bg-[#F6F6F8] tw-transition-colors",
              "hover:tw-border-gray-200 hover:tw-bg-gray-200",
              "focus-visible:tw-ring-2 focus-visible:tw-ring-blue-600",
            )}
            aria-labelledby={id}
          >
            <CustomCalendarIcon />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className={clsx(
              "tw-min-w-[220px] tw-rounded-md tw-bg-white tw-p-1 tw-shadow-md",
              "data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out",
              "data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0",
              "data-[state=closed]:tw-zoom-out-95 data-[state=open]:tw-zoom-in-95",
              "data-[side=bottom]:tw-slide-in-from-top-2 data-[side=left]:tw-slide-in-from-right-2",
              "data-[side=right]:tw-slide-in-from-left-2 data-[side=top]:tw-slide-in-from-bottom-2",
            )}
            sideOffset={5}
          >
            <DropdownMenu.Item asChild>
              <a
                href={ics(calendarEvent)}
                className={clsx(
                  "tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded tw-p-2",
                  "tw-text-sm tw-text-brand-black tw-no-underline",
                  "hover:tw-bg-gray-100 focus:tw-bg-gray-100 active:tw-bg-gray-100",
                )}
              >
                <CalendarIcon className="tw-h-4 tw-w-4" />
                <FormattedMessage id="meetings.calendar" />
              </a>
            </DropdownMenu.Item>

            <DropdownMenu.Item asChild>
              <a
                href={google(calendarEvent)}
                className={clsx(
                  "tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded tw-p-2",
                  "tw-text-sm tw-text-brand-black tw-no-underline",
                  "hover:tw-bg-gray-100 focus:tw-bg-gray-100 active:tw-bg-gray-100",
                )}
                target="_blank"
                rel="noreferrer"
              >
                <GoogleCalendarIcon className="tw-h-4 tw-w-4" />
                Google <FormattedMessage id="meetings.calendar" />
              </a>
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <span
        id={id}
        className="tw-whitespace-nowrap tw-text-center tw-text-[11px] tw-text-brand-gray sm:tw-text-xs"
      >
        <FormattedMessage id="meetings.add_to_calendar" values={{ br: <br /> }} />
      </span>
    </div>
  );
}

function getCalendarTitle(meeting: TMeeting): string {
  return (
    "Встреча с клиническим экспертом 3D Smile, " +
    formatPersonName({ person: meeting.expert, useMiddleName: true })
  );
}

function getCalendarDescription(meeting: TMeeting): string {
  let str = `Ссылка для подключения: ${meeting.zoom_join_url ?? meeting.gmeet_url}\n\n`;
  str += "Тема встречи:\n";

  for (const { theme, question, patient } of meeting.patients) {
    str += formatPersonName({ person: patient }) + ` (ID: ${patient.patient_id})\n`;
    str += `${theme}\n`;
    str += `${question}\n\n`;
  }

  return str;
}

function ActionLink({
  id,
  href,
  icon,
  children,
}: {
  id: string;
  href: string;
  icon: React.ReactElement;
  children: React.ReactChild;
}) {
  return (
    <div className="tw-flex tw-max-w-[70px] tw-flex-col tw-items-center tw-gap-1">
      <a
        href={href}
        rel="noopener noreferrer"
        target="_blank"
        className={clsx(
          "tw-inline-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-2xl",
          "tw-border-none tw-bg-[#F6F6F8] tw-transition-colors",
          "hover:tw-border-gray-200 hover:tw-bg-gray-200",
          "focus-visible:tw-ring-2 focus-visible:tw-ring-blue-600",
        )}
        aria-labelledby={id}
      >
        {icon}
      </a>

      <span id={id} className="tw-text-center tw-text-[11px] tw-text-brand-gray sm:tw-text-xs">
        {children}
      </span>
    </div>
  );
}

function MeetingDetailsLink({ meeting }: { meeting: Loading<TMeeting> }) {
  if (!meeting) {
    return (
      <Skeleton containerClassName="tw-w-full" className="tw-w-full tw-rounded-xl" height={50} />
    );
  }

  return (
    <Button
      variant="empty"
      rounded="extra"
      className={clsx(
        "tw-w-full tw-border-[#F6F6F8] tw-bg-[#F6F6F8] tw-py-3",
        "tw-text-brand-black hover:tw-border-gray-200 hover:tw-bg-gray-200",
      )}
      asChild
    >
      <Link to={`/pages/meetings/enrolled/${meeting.id}`}>
        <FormattedMessage id="meetings.detail" />
      </Link>
    </Button>
  );
}

function MeetingJoinLink({ meeting }: { meeting: Loading<TMeeting> }) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  if (!meeting) {
    return (
      <Skeleton containerClassName="tw-w-full" className="tw-w-full tw-rounded-xl" height={50} />
    );
  }

  if (meeting.is_finished) {
    return null;
  }

  const title = `Встреча с клиническим экспертом: ${formatPersonName({
    person: meeting.expert,
    useMiddleName: true,
    isDoctor: true,
  })}`;

  return (
    <Dialog.Root open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <ZoomRedirectDialog
        event={{ title, zoom_join_url: meeting.zoom_join_url ?? meeting.gmeet_url }}
        onClose={() => setIsDialogOpen(false)}
      />

      <Dialog.Trigger asChild>
        <Button variant="primary" rounded="extra" size="2xl" className="tw-w-full">
          <FormattedMessage id="meetings.join" />
        </Button>
      </Dialog.Trigger>
    </Dialog.Root>
  );
}

function MeetingDateBadges({ meeting }: { meeting: Loading<TMeeting> }) {
  return (
    <div className="tw-flex tw-gap-1">
      {meeting ? (
        <MeetingsTag>{formatRelativeDate(meeting.start_time_utc)}</MeetingsTag>
      ) : (
        <Skeleton className="tw-rounded-3xl" width={70} height={28} />
      )}

      {meeting ? (
        <MeetingsTag>{formatTime(meeting.start_time_utc)}</MeetingsTag>
      ) : (
        <Skeleton className="tw-rounded-3xl" width={70} height={28} />
      )}
    </div>
  );
}

function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const todayDate = new Date();

  const diffDays = calculateDifferenceInDays(date, todayDate);

  // If today, yesterday or tomorrow, return as a string
  if (-1 <= diffDays && diffDays <= 1) {
    return capitalizeFirstLetter(
      new Intl.RelativeTimeFormat(buildLocale, { numeric: "auto" }).format(diffDays, "day"),
    );
  }

  // If not today, yesterday, or tomorrow, format the date
  return new Intl.DateTimeFormat(buildLocale, {
    day: "2-digit",
    month: "long",
    year: date.getFullYear() !== todayDate.getFullYear() ? "numeric" : undefined,
  }).format(date);
}

function calculateDifferenceInDays(date1: Date, date2: Date): number {
  // Set both dates to the start of the day for comparison
  date1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
  date2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());

  // Calculate the difference in days
  return Math.round((date1.getTime() - date2.getTime()) / (1000 * 3600 * 24));
}

function capitalizeFirstLetter(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

function RescheduleIcon(props: React.SVGAttributes<SVGElement>) {
  return (
    <svg
      width="17"
      height="18"
      viewBox="0 0 17 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M11.6706 8.60828L15.5 4.71891M15.5 4.71891L11.6706 1M15.5 4.71891H5.57273C4.14714 4.71891 3.43434 4.71891 2.88984 4.98386C2.41088 5.21691 2.02148 5.58879 1.77744 6.04618C1.5 6.56617 1.5 7.24687 1.5 8.60828V17"
        className="tw-stroke-brand-gray"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CancelIcon(props: React.SVGAttributes<SVGElement>) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M1 9C1 13.4183 4.58172 17 9 17C13.4183 17 17 13.4183 17 9C17 4.58172 13.4183 1 9 1C4.58172 1 1 4.58172 1 9Z"
        stroke="#F05858"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.6668 6.33328L6.3335 11.6666M11.6668 11.6666L6.3335 6.33325"
        stroke="#F05858"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CustomCalendarIcon(props: React.SVGAttributes<SVGElement>) {
  return (
    <svg
      width="16"
      height="18"
      viewBox="0 0 16 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M1 5.58333C0.585786 5.58333 0.25 5.91912 0.25 6.33333C0.25 6.74755 0.585786 7.08333 1 7.08333V5.58333ZM15 7.08333C15.4142 7.08333 15.75 6.74755 15.75 6.33333C15.75 5.91912 15.4142 5.58333 15 5.58333V7.08333ZM1.95532 16.8062L2.30008 16.1401L1.95532 16.8062ZM1.19074 16.0294L0.520338 16.3656H0.520338L1.19074 16.0294ZM14.8094 16.0294L15.4798 16.3656L14.8094 16.0294ZM14.0443 16.8062L13.6995 16.1401L14.0443 16.8062ZM14.0443 2.97154L14.3891 2.30548L14.0443 2.97154ZM14.8094 3.74848L14.139 4.08473L14.8094 3.74848ZM1.95532 2.97154L1.61056 2.30548L1.95532 2.97154ZM1.19074 3.74848L0.520338 3.41223H0.520338L1.19074 3.74848ZM11.1595 10.415C11.4501 10.1198 11.4463 9.64498 11.1511 9.3544C10.8559 9.06382 10.3811 9.06756 10.0905 9.36275L11.1595 10.415ZM7.125 13.4444L6.59051 13.9706C6.73149 14.1138 6.92404 14.1944 7.125 14.1944C7.32596 14.1944 7.51851 14.1138 7.65949 13.9706L7.125 13.4444ZM5.90949 11.1405C5.61891 10.8453 5.14405 10.8416 4.84886 11.1322C4.55367 11.4228 4.54993 11.8976 4.84051 12.1928L5.90949 11.1405ZM12.25 1C12.25 0.585786 11.9142 0.25 11.5 0.25C11.0858 0.25 10.75 0.585786 10.75 1H12.25ZM10.75 2.77778C10.75 3.19199 11.0858 3.52778 11.5 3.52778C11.9142 3.52778 12.25 3.19199 12.25 2.77778H10.75ZM5.25 1C5.25 0.585786 4.91421 0.25 4.5 0.25C4.08579 0.25 3.75 0.585786 3.75 1H5.25ZM3.75 2.77778C3.75 3.19199 4.08579 3.52778 4.5 3.52778C4.91421 3.52778 5.25 3.19199 5.25 2.77778H3.75ZM1 7.08333H15V5.58333H1V7.08333ZM3.80017 3.52778H12.2002V2.02778H3.80017V3.52778ZM14.25 5.62218V14.1555H15.75V5.62218H14.25ZM12.2002 16.25H3.80017V17.75H12.2002V16.25ZM1.75 14.1555V5.62218H0.25V14.1555H1.75ZM3.80017 16.25C3.29757 16.25 2.96632 16.2494 2.71263 16.2283C2.46799 16.208 2.36284 16.1726 2.30008 16.1401L1.61056 17.4723C1.92215 17.6336 2.24938 17.695 2.58856 17.7232C2.91868 17.7506 3.32268 17.75 3.80017 17.75V16.25ZM0.25 14.1555C0.25 14.6412 0.249435 15.0498 0.276256 15.3833C0.303753 15.7252 0.363622 16.0532 0.520338 16.3656L1.86114 15.6931C1.82712 15.6253 1.79162 15.5141 1.77143 15.2631C1.75057 15.0036 1.75 14.6655 1.75 14.1555H0.25ZM2.30008 16.1401C2.11296 16.0433 1.95868 15.8876 1.86114 15.6931L0.520338 16.3656C0.75835 16.8401 1.13912 17.2282 1.61056 17.4723L2.30008 16.1401ZM14.25 14.1555C14.25 14.6656 14.2495 15.0037 14.2287 15.2632C14.2085 15.5143 14.173 15.6254 14.139 15.6931L15.4798 16.3656C15.6366 16.0531 15.6964 15.7251 15.7238 15.3832C15.7506 15.0497 15.75 14.6411 15.75 14.1555H14.25ZM12.2002 17.75C12.6777 17.75 13.0815 17.7506 13.4115 17.7232C13.7506 17.695 14.0776 17.6335 14.3891 17.4723L13.6995 16.1401C13.6367 16.1727 13.5317 16.208 13.2873 16.2283C13.0338 16.2494 12.7028 16.25 12.2002 16.25V17.75ZM14.139 15.6931C14.0418 15.8871 13.8872 16.043 13.6995 16.1401L14.3891 17.4723C14.86 17.2285 15.2416 16.8407 15.4798 16.3656L14.139 15.6931ZM12.2002 3.52778C12.7028 3.52778 13.0338 3.52838 13.2873 3.54943C13.5317 3.56971 13.6367 3.60509 13.6995 3.63761L14.3891 2.30548C14.0776 2.14424 13.7506 2.08273 13.4115 2.05457C13.0815 2.02718 12.6776 2.02778 12.2002 2.02778V3.52778ZM15.75 5.62218C15.75 5.13661 15.7506 4.72799 15.7238 4.39456C15.6964 4.0527 15.6366 3.72475 15.4798 3.41223L14.139 4.08473C14.173 4.1525 14.2085 4.26358 14.2287 4.51458C14.2495 4.77402 14.25 5.11209 14.25 5.62218H15.75ZM13.6995 3.63761C13.8871 3.73468 14.0417 3.89067 14.139 4.08473L15.4798 3.41223C15.2416 2.93727 14.8601 2.54929 14.3891 2.30548L13.6995 3.63761ZM3.80017 2.02778C3.32269 2.02778 2.91868 2.02718 2.58857 2.05457C2.2494 2.08271 1.92216 2.14419 1.61056 2.30548L2.30008 3.63761C2.36283 3.60513 2.46797 3.56973 2.71261 3.54943C2.96631 3.52838 3.29756 3.52778 3.80017 3.52778V2.02778ZM1.75 5.62218C1.75 5.11217 1.75057 4.77409 1.77143 4.5147C1.79161 4.26373 1.82711 4.15258 1.86114 4.08473L0.520338 3.41223C0.363631 3.72467 0.303756 4.05254 0.276257 4.39444C0.249435 4.72792 0.25 5.13654 0.25 5.62218H1.75ZM1.61056 2.30548C1.13904 2.54955 0.75831 2.93777 0.520338 3.41223L1.86114 4.08473C1.95872 3.89017 2.11304 3.73442 2.30008 3.63761L1.61056 2.30548ZM10.0905 9.36275L6.59051 12.9183L7.65949 13.9706L11.1595 10.415L10.0905 9.36275ZM7.65949 12.9183L5.90949 11.1405L4.84051 12.1928L6.59051 13.9706L7.65949 12.9183ZM10.75 1V2.77778H12.25V1H10.75ZM3.75 1V2.77778H5.25V1H3.75Z"
        className="tw-fill-brand-gray"
      />
    </svg>
  );
}

function GoogleCalendarIcon(props: React.SVGAttributes<SVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" {...props}>
      <path d="M152.637 47.363H47.363v105.273h105.273z" fill="#fff"></path>
      <path d="M152.637 200L200 152.637h-47.363z" fill="#f72a25"></path>
      <path d="M200 47.363h-47.363v105.273H200z" fill="#fbbc04"></path>
      <path d="M152.637 152.637H47.363V200h105.273z" fill="#34a853"></path>
      <path
        d="M0 152.637v31.576A15.788 15.788 0 0 0 15.788 200h31.576v-47.363z"
        fill="#188038"
      ></path>
      <path
        d="M200 47.363V15.788A15.79 15.79 0 0 0 184.212 0h-31.575v47.363z"
        fill="#1967d2"
      ></path>
      <path
        d="M15.788 0A15.79 15.79 0 0 0 0 15.788v136.849h47.363V47.363h105.274V0z"
        fill="#4285f4"
      ></path>
      <path
        d="M68.962 129.02c-3.939-2.653-6.657-6.543-8.138-11.67l9.131-3.76c.83 3.158 2.279 5.599 4.346 7.341 2.051 1.742 4.557 2.588 7.471 2.588 2.995 0 5.55-.911 7.699-2.718 2.148-1.823 3.223-4.134 3.223-6.934 0-2.865-1.139-5.208-3.402-7.031s-5.111-2.718-8.496-2.718h-5.273v-9.033h4.736c2.913 0 5.387-.781 7.389-2.376 2.002-1.579 2.995-3.743 2.995-6.494 0-2.441-.895-4.395-2.686-5.859s-4.053-2.197-6.803-2.197c-2.686 0-4.818.716-6.396 2.148s-2.767 3.255-3.451 5.273l-9.033-3.76c1.204-3.402 3.402-6.396 6.624-8.984s7.34-3.89 12.337-3.89c3.695 0 7.031.716 9.977 2.148s5.257 3.418 6.934 5.941c1.676 2.539 2.507 5.387 2.507 8.545 0 3.223-.781 5.941-2.327 8.187-1.546 2.23-3.467 3.955-5.729 5.143v.537a17.39 17.39 0 0 1 7.34 5.729c1.904 2.572 2.865 5.632 2.865 9.212s-.911 6.771-2.718 9.57c-1.823 2.799-4.329 5.013-7.52 6.624s-6.787 2.425-10.775 2.425c-4.622 0-8.887-1.318-12.826-3.988zm56.087-45.312l-10.026 7.243-5.013-7.601 17.985-12.972h6.901v61.198h-9.847z"
        fill="#1a73e8"
      ></path>
    </svg>
  );
}
